"use client";

import Link from "next/link";
import { AlertTriangle, Check, ListTodo } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useTodoActions } from "@/hooks/use-todo-actions";

export function ActionsSummaryCard() {
  const { data: todos, isLoading } = useTodoActions();

  const pending = (todos || []).filter((t) => t.status === "pending");
  const overdue = pending.filter((t) => t.urgency === "overdue");
  const total = pending.length;

  return (
    <Card>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-sm font-bold uppercase tracking-wider">
              Actions
            </h2>
            {total > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {total}
              </Badge>
            )}
          </div>
          <Link
            href="/crm/actions"
            className="text-xs text-blue-500 hover:underline"
          >
            Voir tout →
          </Link>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 rounded-lg" />
            <Skeleton className="h-10 rounded-lg" />
          </div>
        ) : overdue.length > 0 ? (
          <div className="space-y-2">
            <Link
              href="/crm/actions"
              className="flex items-center gap-3 rounded-lg bg-red-500/10 p-3 hover:bg-red-500/15 transition-colors"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-red-600">
                  {overdue.length} action{overdue.length > 1 ? "s" : ""} en
                  retard
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {overdue[0].title}
                  {overdue.length > 1 && ` + ${overdue.length - 1} autre${overdue.length - 1 > 1 ? "s" : ""}`}
                </p>
              </div>
            </Link>
            {total - overdue.length > 0 && (
              <p className="px-1 text-xs text-muted-foreground">
                {total - overdue.length} action
                {total - overdue.length > 1 ? "s" : ""} à venir
              </p>
            )}
          </div>
        ) : total > 0 ? (
          <Link
            href="/crm/actions"
            className="flex items-center gap-3 rounded-lg bg-emerald-500/10 p-3 hover:bg-emerald-500/15 transition-colors"
          >
            <Check className="h-4 w-4 shrink-0 text-emerald-500" />
            <p className="text-sm text-emerald-700">
              {total} action{total > 1 ? "s" : ""} à faire cette semaine
            </p>
          </Link>
        ) : (
          <div className="flex flex-col items-center py-6 text-center">
            <Check className="h-8 w-8 text-emerald-500/40" />
            <p className="mt-2 text-xs text-muted-foreground">
              Aucune action en cours
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
